/**
 * 🌍 TIMEZONE INTERCEPTOR - Solución Central para Fechas
 * =====================================================
 * 
 * Transformador automático que intercepta respuestas de API y corrige
 * automáticamente TODAS las fechas para mostrarlas en timezone del usuario.
 * 
 * EFECTO: 24+ componentes funcionan SIN cambios de código
 */

import { formatForUser } from './timezone';

// Configuración central
const TIMEZONE_CONFIG = {
  // Patterns de campos que son fechas (auto-detección)
  dateFieldPatterns: [
    /.*_at$/i,           // created_at, updated_at, assigned_at
    /.*_date$/i,         // start_date, end_date, birth_date  
    /^timestamp$/i,      // timestamp
    /^date$/i,           // date
    /.*created$/i,       // created
    /.*updated$/i,       // updated
    /last_query$/i,      // last_query
    /next_run$/i,        // next_run
    /generation_date$/i, // generation_date
    /query_date$/i,      // query_date
    /scheduled.*date$/i, // scheduledDate
    /triggered_at$/i,    // triggered_at
    /executed_at$/i,     // executed_at
    /last_activity$/i,   // last_activity
    /first_query$/i,     // first_query
    /startDate$/i,       // startDate
    /endDate$/i,         // endDate
  ],
  
  // Endpoints que NO procesar (para evitar interferencias)
  excludeEndpoints: [
    '/health',
    '/status', 
    '/auth/',
    '/login',
    '/register',
    '/firebase'
  ],
  
  // Debug mode
  debugMode: false,  // Cambiar a true para debugging
  
  // Stats para monitoreo
  stats: {
    intercepted: 0,
    fieldsTransformed: 0,
    errors: 0
  }
};

/**
 * Fields that MUST remain stable/canonical and should never be auto-formatted.
 *
 * Rationale:
 * - Some date-like fields are used as identifiers/keys or are round-tripped back to the API.
 * - Auto-formatting them (e.g., "2026-01-01" -> "01/01/2026, 12:00:00 AM EST") breaks sorting,
 *   URL query params, and backend validation (expects ISO dates).
 */
const DO_NOT_TRANSFORM_DATE_FIELDS = new Set([
  // Financial (cashflow) round-trip keys
  'period_date',
  'effective_period_date',
  'start_period',
  'end_period',
  // Common filter params used as canonical ISO dates
  'start_date',
  'end_date',
]);

/**
 * Verifica si un campo es una fecha basado en patterns
 */
const isDateField = (fieldName) => {
  return TIMEZONE_CONFIG.dateFieldPatterns.some(pattern => pattern.test(fieldName));
};

/**
 * Verifica si un valor ya está formateado (no necesita transformación)
 */
const isAlreadyFormatted = (value) => {
  if (typeof value !== 'string') return false;
  // Check for formatted date patterns (MM/DD/YYYY, HH:MM:SS AM/PM TZ)
  const formattedPattern = /^\d{1,2}\/\d{1,2}\/\d{4},\s*\d{1,2}:\d{1,2}:\d{1,2}\s*(AM|PM)\s+[A-Z]{2,4}$/i;
  return formattedPattern.test(value.trim());
};

/**
 * Verifica si un valor parece ser una fecha/timestamp
 */
const isDateValue = (value) => {
  // Skip null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return false;
  }
  
  if (typeof value !== 'string') return false;
  
  // Patterns de formato de fecha común
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}$/,                    // 2025-07-16
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,  // ISO datetime
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/,  // SQL datetime
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+/, // ISO with milliseconds
  ];
  
  return datePatterns.some(pattern => pattern.test(value));
};

/**
 * Transforma un valor de fecha usando formatForUser
 */
const transformDateValue = (value, fieldName) => {
  try {
    // Skip null, undefined, or empty values
    if (value === null || value === undefined || value === '') {
      return value;
    }
    
    // Skip if value is already formatted (don't double-format)
    if (isAlreadyFormatted(value)) {
      if (TIMEZONE_CONFIG.debugMode) {
        console.log(`🌍 Timezone Transform [${fieldName}]: Skipping already formatted value: "${value}"`);
      }
      return value; // Return as-is, already formatted
    }

    // IMPORTANT STANDARDIZATION:
    // Keep pure dates (YYYY-MM-DD) canonical. They are often used as keys/filters and must round-trip safely.
    // If UI needs a localized display, components should explicitly call DateTimeUtils/TimezoneManager.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    const transformed = formatForUser(value);
    
    if (TIMEZONE_CONFIG.debugMode) {
      console.log(`🌍 Timezone Transform [${fieldName}]: "${value}" → "${transformed}"`);
    }
    
    TIMEZONE_CONFIG.stats.fieldsTransformed++;
    return transformed;
    
  } catch (error) {
    if (TIMEZONE_CONFIG.debugMode) {
      console.warn(`⚠️ Timezone Transform Error [${fieldName}]:`, error);
    }
    TIMEZONE_CONFIG.stats.errors++;
    return value; // Devolver valor original si falla
  }
};

/**
 * Procesa recursivamente un objeto transformando campos de fecha
 */
const processObject = (obj, parentKey = '') => {
  if (obj === null || obj === undefined) return obj;
  
  // Arrays
  if (Array.isArray(obj)) {
    return obj.map((item, index) => processObject(item, `${parentKey}[${index}]`));
  }
  
  // Objetos
  if (typeof obj === 'object') {
    const processed = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      
      // Keep canonical date keys stable (do not format them for display).
      if (DO_NOT_TRANSFORM_DATE_FIELDS.has(key)) {
        processed[key] = processObject(value, fullKey);
        continue;
      }

      // Verificar si es un campo fecha
      if (isDateField(key) && isDateValue(value)) {
        processed[key] = transformDateValue(value, fullKey);
      } else {
        // Procesar recursivamente
        processed[key] = processObject(value, fullKey);
      }
    }
    
    return processed;
  }
  
  // Valores primitivos
  return obj;
};

/**
 * 🎯 INTERCEPTOR PRINCIPAL
 * Función que se llama desde apiCall para transformar automáticamente las fechas
 */
export const autoTransformDates = (data, endpoint = '') => {
  // Verificar si este endpoint debe ser excluido
  const shouldExclude = TIMEZONE_CONFIG.excludeEndpoints.some(excluded => 
    endpoint.includes(excluded)
  );
  
  if (shouldExclude) {
    return data; // No procesar endpoints excluidos
  }
  
  try {
    TIMEZONE_CONFIG.stats.intercepted++;
    
    if (TIMEZONE_CONFIG.debugMode) {
      console.log(`🌍 Timezone Interceptor: Processing endpoint "${endpoint}"`);
    }
    
    const transformed = processObject(data);
    
    if (TIMEZONE_CONFIG.debugMode) {
      console.log(`✅ Timezone Interceptor: Completed. Fields transformed: ${TIMEZONE_CONFIG.stats.fieldsTransformed}`);
    }
    
    return transformed;
    
  } catch (error) {
    console.error('❌ Timezone Interceptor Error:', error);
    TIMEZONE_CONFIG.stats.errors++;
    return data; // Devolver data original si hay error
  }
};

/**
 * Obtiene estadísticas del interceptor (para debugging)
 */
export const getTimezoneStats = () => {
  return { ...TIMEZONE_CONFIG.stats };
};

/**
 * Resetea estadísticas del interceptor
 */
export const resetTimezoneStats = () => {
  TIMEZONE_CONFIG.stats.intercepted = 0;
  TIMEZONE_CONFIG.stats.fieldsTransformed = 0;
  TIMEZONE_CONFIG.stats.errors = 0;
};

/**
 * Habilita/deshabilita modo debug
 */
export const setTimezoneDebugMode = (enabled) => {
  TIMEZONE_CONFIG.debugMode = enabled;
};

// Log de inicialización
if (TIMEZONE_CONFIG.debugMode) {
  console.log('🌍 Timezone Interceptor: Initialized with config:', {
    patterns: TIMEZONE_CONFIG.dateFieldPatterns.length,
    excludeEndpoints: TIMEZONE_CONFIG.excludeEndpoints.length,
    debugMode: TIMEZONE_CONFIG.debugMode
  });
} 